import {DOCUMENT} from '@angular/common';
import {Component, HostBinding, Inject, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {SubscriptionLike} from 'rxjs';
import {filter} from 'rxjs/operators';
import {selectCurrentContextFeature} from './core/redux/current-context/current-context-selectors';
import {CurrentContextState} from './core/redux/current-context/reducer/current-context.reducer';
import {ScreenWidthService} from './core/services/responsive/screen-width.service';
import {TitleService} from './core/services/title/title.service';
import { AlertModalComponent } from './entry-routes/alert-message/components/alert-modal/alert-modal.component';
import { ToasterComponent } from './core/ui/toaster/components/toaster.component';
import { RouterOutlet } from '@angular/router';
import { MaintenanceInfoComponent } from './core/ui/maintenance-info/maintenance-info.component';
import { MsalService } from '@azure/msal-angular';
import {replace} from 'lodash';
import {Constant} from './core/utils/constant';



@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [MaintenanceInfoComponent, RouterOutlet, ToasterComponent, AlertModalComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  currentContext$ = this.store.select(selectCurrentContextFeature);

  @HostBinding('class.mobile') mobile = false;
  @HostBinding('class.desktop') desktop = false;

  title = 'Nexia';
  public screenWidth: number;
  private subs: SubscriptionLike[] = [];
  private savedInitTemplateUrlsMap: Map<string,string>= new Map<string,string>();


  constructor(
    @Inject(DOCUMENT) public document,
    private titleService: TitleService,
    private screenWidthService: ScreenWidthService,
    public translate: TranslateService,
    private store: Store,
    private msalService: MsalService
  ) {
    // translate.setDefaultLang('fr');
    // translate.use('fr');
  }

  ngOnInit() {

    // remplace la balise app-redirect qui était dans template index.html
    this.subs.push(this.msalService.handleRedirectObservable().subscribe());

    // sets the Browser Page title
    this.subs.push(this.titleService.title.subscribe((title) => {
      this.document.title = title;
    }));

    this.subs.push(this.screenWidthService.screenWidth$.subscribe((screenWidth) => {
      let desktop = false;
      let mobile = false;
      this.screenWidth = screenWidth;
      if (this.screenWidth > 1023) {
        desktop = true;
      } else {
        mobile = true;
      }
      this.desktop = desktop;
      this.mobile = mobile;
    }));
    // this.subs.push(this.translate.get('GLOBAL.app_title').subscribe((response: string) => {
    //   this.title = response;
    //   this.titleService.setTitle(this.title);
    // }));

    this.subs.push(this.translate.onLangChange.pipe(
      filter((langChangeEvent: LangChangeEvent) => !!langChangeEvent.lang && !!langChangeEvent.translations)
    ).subscribe(() => {
      this.title = this.translate.instant('GLOBAL.app_title');
      this.titleService.setTitle(this.title);
    }));

    this.subs.push(this.currentContext$.pipe(
      filter((currentContext: CurrentContextState) => !!currentContext && !!currentContext.datas && !!currentContext.datas.currentModule && !currentContext.loading),
    ).subscribe((currentContextState: CurrentContextState) => {
        this.setDynamiqueThemeCss(currentContextState.datas.currentModule);
     }));
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub: SubscriptionLike) => sub.unsubscribe());
  }

  private setDynamiqueThemeCss(currentModule: string) {

    const baseCssPropertiesValues = new Map<string,string>();

    // récupération des valeurs des variables css liées au module
    const regDblQuote = /"/gi;
    const computedStyle = getComputedStyle(document.documentElement);
    Constant.BASE_DYNAMIC_CSS_PROPERTIES_KEYS.forEach((key:string) => {
      baseCssPropertiesValues.set(key,replace(computedStyle.getPropertyValue(key+'-'+currentModule),regDblQuote,''));
    });


    // récupération des valeurs de variable css URL utilisables lié au module
    Constant.BASE_DYNAMIC_URL_CSS_PROPERTIES_KEYS.forEach((key:string) => {
      const urlWithTemplateForKey = this.getCssTemplateUrl(key);
      const url_value_for_module = replace( urlWithTemplateForKey, '[--folder_image_module]', baseCssPropertiesValues.get('--folder_image_module') ) ;
      baseCssPropertiesValues.set(key,url_value_for_module);
    });

    // définition des valeurs de variable css utilisable dans les css
    const documentStyle = document.documentElement.style;
    Constant.BASE_DYNAMIC_CSS_PROPERTIES_KEYS.forEach((key:string) => {
      documentStyle.setProperty(key, baseCssPropertiesValues.get(key));
    });

    Constant.BASE_DYNAMIC_URL_CSS_PROPERTIES_KEYS.forEach((key:string) => {
      documentStyle.setProperty(key, baseCssPropertiesValues.get(key));
    });

  }

  // récupère une url d'image avec template [--folder_image_module]
  // + si l' url contient [--folder_image_module], premier appel ...
  //  alors stockage/sauvegarde dans this.savedInitTemplateUrlsMap pour récupération éventuelle ultérieure
  // sinon récupération depuis la Map this.savedInitTemplateUrlsMap
  private getCssTemplateUrl(name: string) {
    let templateUrl='';
    const computedStyle = getComputedStyle(document.documentElement);
    if ( computedStyle.getPropertyValue(name).includes('[--folder_image_module]')) {
      templateUrl = computedStyle.getPropertyValue(name);
      this.savedInitTemplateUrlsMap.set(name,templateUrl);
    } else {
      templateUrl=this.savedInitTemplateUrlsMap.get(name);
    }
    return templateUrl;
  }
}
